import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/authContext";
import {
  ApplicationPage,
  ApplicationSectionHeader,
  ApplicationStateRegion,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../../components/application";
import useProjectFacilitatorOverview from "../hooks/useProjectFacilitatorOverview";
import ProjectFacilitatorNavigation from "../components/ProjectFacilitatorNavigation";
import ProgramEvidenceComponents from "../evidence/ProgramEvidenceComponents";
import { resolveProgramFacilitatePresentationAdapter } from "../evidence/programFacilitatePresentationAdapterRegistry";
import { projectInstitutionalContextLabel } from "../utils/projectPresentation";

const statusItems = (program) => [
  ["Participation not established", program.progress.applicability_unknown],
  ["In progress", program.progress.in_progress],
  ["Completed", program.progress.completed],
];

export default function ProjectFacilitatorOverview() {
  const { slug } = useParams();
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001/api";
  const { overview, evidenceByProgram = {}, loading, error } = useProjectFacilitatorOverview({ apiUrl, slug, token: user?.token });
  const [selectedProgramId, setSelectedProgramId] = useState("");

  useEffect(() => {
    if (overview?.programs?.length && !overview.programs.some((item) => item.id === selectedProgramId)) {
      setSelectedProgramId(overview.programs[0].id);
    }
  }, [overview, selectedProgramId]);

  if (loading) return <ApplicationPage><ApplicationStateRegion message="Loading Project overview..." status="loading" /></ApplicationPage>;
  if (error || !overview) return (
    <ApplicationPage>
      <ApplicationStateRegion message={error || "Project overview is unavailable."} status="error" />
      <Link className="av-app-button av-app-button--secondary" to="/app/facilitate">Back to Facilitate</Link>
    </ApplicationPage>
  );

  const selectedProgram = overview.programs.find((item) => item.id === selectedProgramId)
    || overview.programs[0];
  const contexts = [
    ...(overview.context?.cohorts || []),
    ...(overview.context?.groups || []),
  ].filter((item) => item?.id && item?.name);
  const presentationAdapter = resolveProgramFacilitatePresentationAdapter(selectedProgram);
  const ProgramOverview = presentationAdapter?.OverviewComponent;

  return (
    <ApplicationPage className="av-project-facilitator-overview" variant="workspace">
      <ApplicationViewHeader
        contextLabel="Project facilitation"
        title={overview.project.title}
        description={projectInstitutionalContextLabel(overview.project) || undefined}
        actions={overview.capabilities?.participantView ? (
          <Link
            className="av-app-button av-app-button--secondary av-app-button--compact"
            to={`/app/project/${slug}`}
          >
            Participant view
          </Link>
        ) : null}
      />
      <Link className="av-project-facilitator-overview__back" to="/app/facilitate">Back to Facilitate</Link>
      <ProjectFacilitatorNavigation slug={slug} showCohortResults={Boolean(presentationAdapter?.key === "ksu-building-your-clinical-ear.facilitate-presentation@1")} />

      <section aria-labelledby="facilitator-overview-summary-title">
        <ApplicationSectionHeader title={presentationAdapter?.overviewSectionTitle || "Overview"} titleId="facilitator-overview-summary-title" />
        {ProgramOverview ? <ProgramOverview
          participants={overview.participants || []}
          program={selectedProgram}
          projectEvidence={evidenceByProgram[selectedProgram.id]}
          projectId={overview.project.id}
          projectSlug={slug}
        /> : <div className="av-project-facilitator-overview__summary-grid">
          <ApplicationSurface variant="nested">
            <p className="av-project-facilitator-overview__metric">{overview.authorizedPopulation.memberCount}</p>
            <p>Project members</p>
          </ApplicationSurface>
          <ApplicationSurface variant="nested">
            <p className="av-project-facilitator-overview__metric">{overview.programs.length}</p>
            <p>{overview.programs.length === 1 ? "Program" : "Programs"}</p>
          </ApplicationSurface>
        </div>}
      </section>

      {!presentationAdapter && selectedProgram && evidenceByProgram[selectedProgram.id] ? (
        <section aria-labelledby="facilitator-program-evidence-title">
          <ApplicationSectionHeader title="Program evidence and readiness" titleId="facilitator-program-evidence-title" description="Program-owned evidence with explicit availability; Facilitate does not infer unavailable methodology." />
          <ProgramEvidenceComponents evidence={evidenceByProgram[selectedProgram.id]} heading={selectedProgram.title} />
        </section>
      ) : null}

      {!presentationAdapter?.configuredProgress?.hidden ? <section aria-labelledby="facilitator-program-progress-title">
        {presentationAdapter?.configuredProgress?.secondary ? <details className="av-program-adapter-secondary"><summary id="facilitator-program-progress-title">{presentationAdapter.configuredProgress.summary}</summary><p>{presentationAdapter.configuredProgress.description}</p>
        <div>
          {selectedProgram ? <ApplicationSurface className="av-project-facilitator-overview__program" variant="nested">
            <h3>{selectedProgram.title}</h3>
            <dl className="av-project-facilitator-overview__status-grid">{statusItems(selectedProgram).map(([label, count]) => <div key={label}><dt>{label}</dt><dd>{count}</dd></div>)}</dl>
          </ApplicationSurface> : null}
        </div></details> : <>
        <ApplicationSectionHeader title="Program progress" titleId="facilitator-program-progress-title" />
        {overview.programs.length > 1 ? (
          <label className="av-project-facilitator-overview__selector">
            <span>Program</span>
            <select value={selectedProgramId} onChange={(event) => setSelectedProgramId(event.target.value)}>
              {overview.programs.map((program) => <option key={program.id} value={program.id}>{program.title}</option>)}
            </select>
          </label>
        ) : null}
        {selectedProgram ? (
          <ApplicationSurface className="av-project-facilitator-overview__program" variant="nested">
            <h3>{selectedProgram.title}</h3>
            {selectedProgram.version ? <p className="av-project-facilitator-overview__metadata">Version {selectedProgram.version}</p> : null}
            <dl className="av-project-facilitator-overview__status-grid">
              {statusItems(selectedProgram).map(([label, count]) => (
                <div key={label}><dt>{label}</dt><dd>{count}</dd></div>
              ))}
              {selectedProgram.historicalAttemptCount ? (
                <div><dt>Historical attempts</dt><dd>{selectedProgram.historicalAttemptCount}</dd></div>
              ) : null}
            </dl>
            {selectedProgram.lastActivityAt ? (
              <p className="av-project-facilitator-overview__metadata">
                Recent activity: {new Date(selectedProgram.lastActivityAt).toLocaleDateString()}
              </p>
            ) : null}
          </ApplicationSurface>
        ) : (
          <ApplicationStateRegion message="This Project has no active Programs." status="empty" />
        )}</>}
      </section> : null}

      {contexts.length && !presentationAdapter?.suppressProjectContext ? (
        <section aria-labelledby="facilitator-project-context-title">
          <ApplicationSectionHeader title="Project context" titleId="facilitator-project-context-title" />
          <ApplicationSurface variant="nested">
            <ul className="av-project-facilitator-overview__context-list">
              {contexts.map((item) => <li key={item.id}>{item.name}</li>)}
            </ul>
          </ApplicationSurface>
        </section>
      ) : null}
    </ApplicationPage>
  );
}
